import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GetShowcases, GetShowcaseItems, GetShowcaseCategories } from "../../../api/queries";
import { Showcase, ShowcaseItem, ShowcaseCategory } from "../../../api/zod-types";
import CategoryCard from "../CategoryCard";
import ProductNotFound from "../ProductNotFound";
import CTAExploreMore from "../CTAExploreMore";
import { SafeLink } from "@/components/ui/SafeLink";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";



function slugify(str: string) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function ShowcaseGrid() {
    

    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const { category, subCategory } = useParams<{ category: string; subCategory: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    function handlePrefetchShowcaseItems(showcasename: string) {
        queryClient.prefetchQuery({
            queryKey: ["showcaseitems", showcasename],
            queryFn: GetShowcaseItems,
        });
    }

    // Fetch categories, showcases, and items
    const { data: catData } = useQuery({
        queryKey: ["showcasecategories"],
        queryFn: GetShowcaseCategories,
        staleTime: 5 * 60 * 1000,
    });

    const { data: showcasesData, isLoading: showcasesLoading } = useQuery({
        queryKey: ["showcases", category, subCategory],
        queryFn: GetShowcases,
        staleTime: 60 * 1000 * 5,
    });

    const { data: itemsData } = useQuery({
        queryKey: ["showcaseitems"],
        queryFn: GetShowcaseItems,
        staleTime: 60 * 1000 * 5,
    });

    if (showcasesLoading) return <div>Loading...</div>;
    // Parse categories
    let categories: ShowcaseCategory[] = [];
    if (catData?.data) {
        let parsed = typeof catData.data === "string" ? JSON.parse(catData.data) : catData.data;
        categories = parsed?.results || [];
    }

    // Find the selected level 1 category object
    const selectedLevel1Category = categories.find(
        cat => cat.categorylevel === "1" && slugify(cat.categoryname) === slugify(subCategory || "")
    );

    // Parse showcases
    const showcases: Showcase[] =
        showcasesData && Array.isArray(showcasesData.data?.results)
            ? showcasesData.data.results
            : [];

    // Filter showcases by subCategory
    // const filteredShowcases = showcases.filter(
    //     s => s.showcasecategoryname &&
    //          s.showcasecategoryname.toLowerCase() === (subCategory || "").toLowerCase()
    // );
    // const filteredShowcases = showcases.filter(
    //     s => s.showcasecategoryname &&
    //          s.showcasecategoryname.trim().toLowerCase().includes((subCategory || "").trim().toLowerCase())
    // );
    // Filter showcases by exact match to selected level 1 category name
    const filteredShowcases = showcases.filter(
        s => selectedLevel1Category &&
            s.showcasecategoryname &&
            s.showcasecategoryname.trim().toLowerCase() === selectedLevel1Category.categoryname.trim().toLowerCase()
    );
    console.log("Filtered showcases for subCategory:", subCategory, filteredShowcases);
    // Deduplicate by showcasename
    const uniqueShowcases = Object.values(
        filteredShowcases.reduce((acc, curr) => {
            acc[curr.showcasename] = curr;
            return acc;
        }, {} as Record<string, Showcase>)
    );
    console.log("Unique showcases to display:", uniqueShowcases);
    // Parse items
    const items: ShowcaseItem[] =
        itemsData && Array.isArray(itemsData.data?.results)
            ? itemsData.data.results
            : [];

    // Helper to count items for each showcase
    const getItemCountForShowcase = (showcasename: string) =>
        items.filter(item => item.showcasename === showcasename).length;

    // Helper to count unique items for each showcase
    const getUniqueItemCountForShowcase = (showcasename: string) => {
        const uniqueProducts = items
            .filter(item => item.showcasename === showcasename)
            .reduce((acc, curr) => {
                acc[curr.productid] = true;
                return acc;
            }, {} as Record<string, boolean>);
        return Object.keys(uniqueProducts).length;
    };

    // Breadcrumb
    const breadcrumb = (
        <nav className="text-sm mb-6" aria-label="Breadcrumb">
            <ol className="flex flex-wrap gap-2 text-gray-500">
            <li >
                <SafeLink className="hover:underline " to="/">Home</SafeLink>
                
            </li>
            <li>/</li>
            <li >
                <SafeLink className="hover:underline " to="/products">Products</SafeLink>
                
            </li>
            <li>/</li>
            {category && (
                <li >
                    <SafeLink
                        className="hover:underline "
                        to={`/products/${category}`}
                    >
                        {category.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </SafeLink>
                    
                </li>
                
            )}
            <li>/</li>
            {subCategory && (
                <li className=" text-black font-semibold  ">
                    {selectedLevel1Category?.categoryname
                        ? selectedLevel1Category.categoryname
                        : subCategory.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                    }
                </li>
            )}
        </ol>
        </nav>
    );
    // UI: Show ProductNotFound if no showcases
    if (uniqueShowcases.length === 0) {
        return (
            <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen px-2 justify-center items-center">
                <div className="max-w-lg w-full mx-auto">
                    {breadcrumb}
                    <ProductNotFound
                        title="No Products Found"
                        message="Sorry, we couldn't find any products for this category."
                        buttonText="Go Back"
                        onGoBack={() => navigate(`/products/${category}`)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full bg-gradient-to-b from-[#f8fafc] to-[#f2f2f2] pb-12 min-h-screen ">
            {/* <div className="w-full max-w-4xl mx-auto mt-10 mb-8">
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center mb-4 tracking-tight capitalize">
                    {subCategory?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </h1>
                <p className="text-lg md:text-xl text-gray-600 text-center mb-6">
                    Discover our range of {subCategory?.replace(/-/g, " ").toLowerCase()} products.
                </p>

            </div> */}
            
            {/* Hero Section - full width, background image, overlay */}
            <section
                className="relative w-full h-[340px] md:h-[420px] flex items-center justify-center mb-8"
                style={
                    selectedLevel1Category?.categoryimageurl
                        ? {
                            backgroundImage: `url(${selectedLevel1Category.categoryimageurl})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }
                        : {
                            backgroundColor: "#111",
                        }
                }
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
                {/* Decorative elements (optional) */}
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-red-400 rounded-full animate-ping opacity-60"></div>
                <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white rounded-full animate-pulse opacity-80"></div>
                {/* Main hero content */}
                <div className="relative z-10 flex flex-col items-center justify-center w-full h-full text-center px-4">
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold py-4 text-white animate-fade-in-up delay-200">
                        {selectedLevel1Category?.categoryname
                            ? selectedLevel1Category.categoryname
                            : subCategory?.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </h1>
                    <p className="text-lg md:text-xl text-white/90 py-4 animate-fade-in-up delay-400 max-w-2xl mx-auto">
                        {selectedLevel1Category?.categoryname
                            ? `Discover our range of ${selectedLevel1Category.categoryname.toLowerCase()} products. Find the perfect product for your needs.`
                            : `Discover our range of ${subCategory?.replace(/-/g, " ").toLowerCase()} products.`}
                    </p>
                </div>
            </section>
            <div className="w-full max-w-6xl mx-auto mt-4 px-4 md:px-8 lg:px-16 xl:px-32  mb-8 ">
                {breadcrumb}

            </div>
            <div className="w-full max-w-6xl mx-auto mt-4 px-4 md:px-8 lg:px-16 xl:px-32  mb-8">

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-2 md:px-4 lg:px-8 xl:px-12">
                    {uniqueShowcases.map((showcase, idx) => (
                        <CategoryCard
                            key={showcase.showcaseid}
                            title={showcase.showcasename}
                            image={showcase.showcaseimageurl}
                            url={`/products/${category}/${subCategory}/${showcase.showcasename}`}
                            onClick={() => navigate(`/products/${category}/${subCategory}/${showcase.showcasename}`)}
                            onMouseEnter={() => handlePrefetchShowcaseItems(showcase.showcasename)}
                            // productCount={getItemCountForShowcase(showcase.showcasename)}
                            productCount={getUniqueItemCountForShowcase(showcase.showcasename)}
                            animateClassName="animate-fade-in-up"
                            animationDelay={`${idx * 100}ms`}
                        />
                    ))}
                </div>


                <div className="flex justify-center mt-8">
                    <CTAExploreMore
                        text={category?.replace(/-/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase()) || "Sub-Categories"}
                        suffix="Products"
                        to={`/products/${category}`}
                    />
                </div>

            </div>
        </div>

    );
}